/**
 * MorningRitualCard — светлина → вода → движение, преди кофеина (Nerve2).
 * Данни: GET /api/cognitive/morning-ritual, POST .../done
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Circle, Flame, Loader2, Sunrise } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MorningRitualCard({ isDark = false, cardCls = "" }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const border = isDark ? "border-white/10" : "border-gray-200";

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/cognitive/morning-ritual`)
      .then((r) => { if (alive) setD(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!d) return null;

  const toggle = async (key) => {
    const done = d.steps.filter((s) => s.done).map((s) => s.key);
    const next = done.includes(key) ? done.filter((k) => k !== key) : [...done, key];
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/cognitive/morning-ritual/done`, { done: next });
      setD(data);
    } catch { toast.error("Не се записа"); }
    finally { setBusy(false); }
  };

  const doneCount = d.steps.filter((s) => s.done).length;

  return (
    <div className={cardCls} data-testid="morning-ritual-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-bold inline-flex items-center gap-1.5">
            <Sunrise className="w-4 h-4 text-amber-500" /> Сутрешен ритуал за будност
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin opacity-60" />}
          </div>
          <p className="text-[12px] opacity-80 mt-1">{d.intro_bg}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-2xl font-extrabold tabular-nums" data-testid="morning-ritual-progress">
            {doneCount}/{d.steps.length}
          </div>
          <div className="text-[10px] opacity-70 inline-flex items-center gap-1"
            data-testid="morning-ritual-streak">
            <Flame className="w-3 h-3 text-orange-500" /> {d.streak.days} дни поред
          </div>
        </div>
      </div>

      <div className="mt-3 space-y-1.5">
        {d.steps.map((s, i) => (
          <button key={s.key} type="button" onClick={() => toggle(s.key)} disabled={busy}
            role="checkbox" aria-checked={s.done} data-done={s.done ? "1" : "0"}
            className={`w-full text-left p-2.5 rounded-xl border transition-colors ${
              s.done ? "border-emerald-500/40 bg-emerald-500/10" : border}`}
            data-testid={`morning-ritual-step-${s.key}`}>
            <div className="flex items-start gap-2">
              {s.done ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-500" />
                      : <Circle className="w-4 h-4 mt-0.5 shrink-0 opacity-40" />}
              <div className="min-w-0">
                <div className="text-[12px] font-bold">
                  {i + 1}. {s.title_bg}
                  <span className="ml-1.5 font-medium opacity-50">{s.minute_bg}</span>
                </div>
                <div className="text-[11px] opacity-85 mt-0.5">{s.what_bg}</div>
                <div className="text-[11px] opacity-70 mt-0.5">{s.why_bg}</div>
                {s.personal_bg && (
                  <div className="text-[11px] font-medium mt-1 text-amber-600 dark:text-amber-400"
                    data-testid={`morning-ritual-personal-${s.key}`}>
                    {s.personal_bg}
                  </div>
                )}
                {s.caution_bg && (
                  <div className="text-[11px] mt-1 inline-flex items-start gap-1 text-rose-500"
                    data-testid={`morning-ritual-caution-${s.key}`}>
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {s.caution_bg}
                  </div>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
      <p className="text-[9px] opacity-50 mt-2">{d.note_bg}</p>
    </div>
  );
}
